import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { CustomerLifecycleManagementOrg } from '@wetags/customer-lifecycle-management';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { OfficeUserName, OfficeUserNameWithAi, generateTrackingId } from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { AiOutcomeComponent, CreditUsageLabel } from '@frontend/phone-ui';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore, useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Accordion, Button, Chip, NakedUl, Text } from '@frontend/design-system';
import { getOfficeUser, getStatusChipColor } from '../../utils/call-insight-panel-util';
import { PhoneTreePreview } from '../phone-tree-preview';

type CallDetailsSectionProps = {
  call: HydratedCallRecord;
  onClick: (call: HydratedCallRecord) => void;
};

/**
 * @deprecated This component is deprecated and will be removed in future. Please use {@link CallDetailsList} and {@link CallActivity} components instead.
 */
export const CallDetailsSection = ({ call, onClick }: CallDetailsSectionProps) => {
  const { t } = useTranslation('call-insight-panel');

  const { getScopeName, selectedLocationIds } = useAppScopeStore();

  const { startedAt, direction, status = '', locationId, callerNumber, dialedNumber, user, phoneTrees } = call;

  const statusChipColor = getStatusChipColor(status);

  const isMulti = selectedLocationIds.length > 1;

  const isOutboundCall = direction === 'outbound';

  const outcome = call.aiMetadata?.aiOutcome ?? call.callIntelInsights?.aiOutcome;
  const reason = call.aiMetadata?.aiCallReason;

  const { isActive: hasVoiceAi } = useVoiceAIRCustomizationFlag(selectedLocationIds);
  const officeUserName = getOfficeUser(call, '-');

  return (
    <>
      <Accordion.Header title={t('About This Call')} />
      <Accordion.Body>
        <NakedUl css={styles.list}>
          <li>
            <Text weight='bold'>{t('Call Details')}</Text>
          </li>

          {isMulti && locationId && (
            <li>
              <Text size='medium' color='subdued'>
                {t('Location')}
              </Text>
              <Chip.Location maxWidth='300'>{getScopeName(locationId)}</Chip.Location>
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Time')}
            </Text>
            <Text size='medium'>{dayjs(startedAt).format('MMM D, YYYY, h:mm A')}</Text>
          </li>

          {hasVoiceAi && outcome && outcome !== AIOutcome.AI_OUTCOME_UNKNOWN && (
            <li>
              <Text size='medium' color='subdued' css={{ alignSelf: 'start' }}>
                {t('AI Call Outcome')}
              </Text>
              <AiOutcomeComponent outcome={outcome} info={reason} />
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Result')}
            </Text>
            {direction === 'outbound' && <span>-</span>}
            {direction === 'inbound' && (
              <Chip isResponsive variant={statusChipColor}>
                {startCase(status)}
              </Chip>
            )}
            {!direction && <span>-</span>}
          </li>

          {!isOutboundCall && <Divider />}

          {!isOutboundCall && (
            <li>
              <Text weight='bold'>{t('Call Path Overview')}</Text>
            </li>
          )}

          <li>
            <Text size='medium' color='subdued'>
              {t('Office Number')}
            </Text>
            <Text size='medium'>
              {direction
                ? formatPhoneNumber(direction === 'outbound' ? callerNumber : dialedNumber)
                : formatPhoneNumber(call.callIntelInsights?.officeNumber?.nationalNumber?.toString())}
            </Text>
          </li>

          <li>
            <Text size='medium' color='subdued'>
              {t('Office User')}
            </Text>
            {hasVoiceAi ? (
              <OfficeUserNameWithAi
                displayName={officeUserName}
                profileName={officeUserName}
                userId={user?.userId || ''}
                outcome={outcome}
                showAvatar={officeUserName !== '-'}
              />
            ) : (
              <OfficeUserName
                displayName={officeUserName}
                profileName={officeUserName}
                userId={user?.userId || ''}
                showAvatar={officeUserName !== '-'}
              />
            )}
          </li>

          {!isOutboundCall && !!phoneTrees && phoneTrees.length > 0 && (
            <li>
              <Text size='medium' color='subdued'>
                {t('Phone Tree')}
              </Text>
              <PhoneTreePreview ivrEvents={phoneTrees} />
            </li>
          )}

          {!isOutboundCall && (
            <>
              <li>
                <Button
                  onClick={() => onClick(call)}
                  disabled={isOutboundCall}
                  variant='tertiary'
                  size='large'
                  label={t('View Call Path')}
                  showLabelWhenDisabled
                  hoverLabel={isOutboundCall && t('Not available for outbound calls.')}
                  trackingId={generateTrackingId({
                    prefix: 'CallInsights',
                    component: 'AboutThisCall',
                    context: 'ViewCallPath',
                  })}
                >
                  {t('View Call Path')}
                </Button>
              </li>
            </>
          )}
        </NakedUl>
      </Accordion.Body>
    </>
  );
};

export const CallDetailsList: FC<{ call: HydratedCallRecord; containerStyles?: Interpolation<Theme> }> = ({
  call,
  containerStyles,
}) => {
  const { t } = useTranslation('call-insight-panel');
  const { getScopeName, selectedLocationIds } = useAppScopeStore();
  const { getCustomizationFlagValue } = useAppFlagStore();
  const { startedAt, direction, status = '', locationId, callerNumber, dialedNumber, user } = call;
  const [isCreditEnforcementEnabled] = CustomerLifecycleManagementOrg.useWetag('air-credit-enforcement');

  const statusChipColor = getStatusChipColor(status);

  const isOutboundCall = direction === 'outbound';

  const outcome = call.aiMetadata?.aiOutcome ?? call.callIntelInsights?.aiOutcome;
  const reason = call.aiMetadata?.aiCallReason;

  const hasVoiceAi = getCustomizationFlagValue({
    locationIds: locationId ? [locationId] : [],
    featureEnum: Feature.TRUELARK_AI_VOICE,
  });

  const officeUserName = getOfficeUser(call, '-');
  const isMulti = selectedLocationIds.length > 1;
  const locationName = locationId ? getScopeName(locationId) : t('Unknown Location');

  let aiCallCreditUsed: 'used' | 'notUsed' | 'out' | undefined = undefined;
  const aiCallCreditStatus = call.aiCallCreditStatus;
  if (aiCallCreditStatus === 'AI_CALL_CREDIT_STATUS_CHARGED') {
    aiCallCreditUsed = 'used';
  } else if (aiCallCreditStatus === 'AI_CALL_CREDIT_STATUS_NOT_CHARGED') {
    aiCallCreditUsed = 'notUsed';
  } else if (aiCallCreditStatus === 'AI_CALL_CREDIT_STATUS_OUT_OF_CREDITS') {
    aiCallCreditUsed = 'out';
  }

  return (
    <NakedUl css={[styles.list, containerStyles]}>
      {isMulti && locationId && (
        <li>
          <Text size='medium' color='subdued'>
            {t('Location')}
          </Text>
          <Chip.Location tooltipLabel={locationName} maxWidth='250px'>
            {locationName}
          </Chip.Location>
        </li>
      )}

      <li>
        <Text size='medium' color='subdued'>
          {t('Time')}
        </Text>
        <Text size='medium'>{dayjs(startedAt).format('MMM D, YYYY, h:mm A')}</Text>
      </li>

      {hasVoiceAi && outcome && outcome !== AIOutcome.AI_OUTCOME_UNKNOWN && (
        <li>
          <Text size='medium' color='subdued' css={{ alignSelf: 'start' }}>
            {t('AI Call Outcome')}
          </Text>
          <AiOutcomeComponent outcome={outcome} info={reason} />
        </li>
      )}

      <li>
        <Text size='medium' color='subdued'>
          {t('Result')}
        </Text>
        {!direction || isOutboundCall ? (
          <span>-</span>
        ) : (
          <Chip isResponsive variant={statusChipColor}>
            {startCase(status)}
          </Chip>
        )}
      </li>

      <li>
        <Text size='medium' color='subdued'>
          {t('Office Number')}
        </Text>
        <Text size='medium'>
          {direction
            ? formatPhoneNumber(direction === 'outbound' ? callerNumber : dialedNumber)
            : formatPhoneNumber(call.callIntelInsights?.officeNumber?.nationalNumber?.toString())}
        </Text>
      </li>

      <li>
        <Text size='medium' color='subdued'>
          {t('Office User')}
        </Text>
        {hasVoiceAi ? (
          <OfficeUserNameWithAi
            displayName={officeUserName}
            profileName={officeUserName}
            userId={user?.userId || ''}
            outcome={outcome}
            showAvatar={officeUserName !== '-'}
          />
        ) : (
          <OfficeUserName
            displayName={officeUserName}
            profileName={officeUserName}
            userId={user?.userId || ''}
            showAvatar={officeUserName !== '-'}
          />
        )}
      </li>

      {hasVoiceAi && isCreditEnforcementEnabled && (
        <li>
          <Text size='medium' color='subdued'>
            {t('AI Call Credit')}
          </Text>
          <CreditUsageLabel aiCallCreditUsed={aiCallCreditUsed} />
        </li>
      )}
    </NakedUl>
  );
};

export const CallActivity: FC<{ phoneTrees: HydratedCallRecord['phoneTrees'] }> = ({ phoneTrees }) => {
  const { t } = useTranslation('call-insight-panel');

  if (!phoneTrees || phoneTrees.length === 0) {
    return <Text color='subdued'>{t('No phone tree data available.')}</Text>;
  }

  return (
    <div css={styles.activityRow}>
      <Text size='medium' color='subdued'>
        {t('Phone Tree')}
      </Text>
      <PhoneTreePreview ivrEvents={phoneTrees} />
    </div>
  );
};

const styles = {
  list: css`
    li {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin-bottom: ${theme.spacing(2)};

      .data-label {
        color: ${theme.colors.neutral50};
        text-wrap: nowrap;
      }
    }

    li:last-child {
      margin-bottom: ${theme.spacing(0)};
    }
  `,
  activityRow: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
};
